/**
 * Tervezetek kezelése dialógus (admin oldal).
 *
 * Lekéri a függőben lévő `shift_drafts` rekordokat (opcionálisan
 * telephely + dátum szűréssel), és napokra/dolgozókra csoportosítva
 * jeleníti meg. Minden tervezet mellett **Jóváhagy** és **Elutasít**
 * gomb. A jóváhagyás azonnal beosztássá konvertálja az adott napra,
 * felülírva a már létező műszakokat.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, X, Clock, MapPin, User, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const HU_DAYS = ["vasárnap", "hétfő", "kedd", "szerda", "csütörtök", "péntek", "szombat"];
const HU_MONTHS = ["január", "február", "március", "április", "május", "június", "július", "augusztus", "szeptember", "október", "november", "december"];

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return `${d.getFullYear()}. ${HU_MONTHS[d.getMonth()]} ${d.getDate()}. (${HU_DAYS[d.getDay()]})`;
};

export default function DraftsManagerDialog({
  open,
  onOpenChange,
  siteId,
  from,
  to,
  onChanged,
}) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: "pending" });
      if (siteId) params.set("site_id", siteId);
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const { data } = await api.get(`/shift-drafts?${params.toString()}`);
      setDrafts(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e) || "Tervezetek betöltése sikertelen.");
    } finally {
      setLoading(false);
    }
  }, [siteId, from, to]);

  useEffect(() => {
    if (open) {
      load();
    }
  }, [open, load]);

  const grouped = useMemo(() => {
    const map = new Map();
    drafts.forEach((d) => {
      if (!map.has(d.date)) map.set(d.date, []);
      map.get(d.date).push(d);
    });
    return Array.from(map.entries()).sort(([a], [b]) => (a < b ? -1 : 1));
  }, [drafts]);

  const onApprove = async (id) => {
    setActionId(id);
    try {
      await api.post(`/shift-drafts/${id}/approve`);
      toast.success("Tervezet jóváhagyva — műszak létrehozva.");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e) || "Jóváhagyás sikertelen.");
    } finally {
      setActionId(null);
    }
  };

  const onReject = async (id) => {
    setActionId(id);
    try {
      await api.post(`/shift-drafts/${id}/reject`);
      toast.success("Tervezet elutasítva.");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e) || "Elutasítás sikertelen.");
    } finally {
      setActionId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="drafts-manager-dialog"
        className="max-h-[88vh] max-w-2xl overflow-hidden p-0"
      >
        <DialogHeader className="border-b border-border px-6 pb-4 pt-5">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold">
            Beosztás tervezetek
            {drafts.length > 0 && (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 font-mono text-[11px] font-semibold tabular-nums text-amber-700 ring-1 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900/40">
                {drafts.length} függőben
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-[12.5px] text-muted-foreground">
            A dolgozók által leadott javaslatok. Jóváhagyás esetén a tervezet azonnal
            beosztássá válik (felülírja az adott napi meglévő műszakot).
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="grid place-items-center py-16">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : drafts.length === 0 ? (
            <div className="grid place-items-center rounded-2xl border border-dashed border-border bg-muted/30 py-12 text-center">
              <p className="text-[13.5px] font-medium text-foreground">
                Nincs függőben lévő tervezet
              </p>
              <p className="mt-1 text-[12px] text-muted-foreground">
                A dolgozók a portálon adhatnak le javaslatokat.
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              {grouped.map(([date, items]) => (
                <div key={date}>
                  <p className="mb-2 text-[11.5px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    {fmtDate(date)}
                  </p>
                  <div className="space-y-2">
                    {items.map((d) => (
                      <DraftCard
                        key={d.id || d._id}
                        draft={d}
                        loading={actionId === (d.id || d._id)}
                        onApprove={() => onApprove(d.id || d._id)}
                        onReject={() => onReject(d.id || d._id)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DraftCard({ draft, loading, onApprove, onReject }) {
  return (
    <div
      data-testid={`draft-card-${draft.id || draft._id}`}
      className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card px-4 py-3"
    >
      <div className="min-w-0 flex-1">
        <p className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold tracking-tight">
          <User className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={2} />
          {draft.employee_name || "—"}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
          <span className="inline-flex items-center gap-1 font-mono tabular-nums">
            {draft.start_time}–{draft.end_time}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" strokeWidth={2} />
            {Number(draft.duration_hours || 0).toFixed(1)}h
          </span>
          {draft.site_name && (
            <span className="inline-flex items-center gap-1 truncate">
              <MapPin className="h-3 w-3" strokeWidth={2} />
              {draft.site_name}
            </span>
          )}
          {draft.template_name && (
            <span className="rounded-full bg-foreground/[0.05] px-2 py-0.5 text-[11px] font-medium">
              {draft.template_name}
            </span>
          )}
        </div>
        {draft.notes && (
          <p className="mt-1 inline-flex items-start gap-1 text-[11.5px] italic text-muted-foreground">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" strokeWidth={2} />
            {`„${draft.notes}”`}
          </p>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        <Button
          size="sm"
          variant="ghost"
          className="h-8 gap-1 px-2.5 text-[12px] text-muted-foreground hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-950/30"
          onClick={onReject}
          disabled={loading}
          data-testid={`draft-reject-${draft.id || draft._id}`}
        >
          <X className="h-3.5 w-3.5" strokeWidth={2} />
          Elutasít
        </Button>
        <Button
          size="sm"
          className="h-8 gap-1 bg-gradient-to-br from-emerald-600 to-emerald-700 px-3 text-[12px] font-semibold text-white shadow-[0_4px_12px_-4px_rgba(16,185,129,0.5)] hover:from-emerald-700 hover:to-emerald-800"
          onClick={onApprove}
          disabled={loading}
          data-testid={`draft-approve-${draft.id || draft._id}`}
        >
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
          )}
          Jóváhagy
        </Button>
      </div>
    </div>
  );
}
